import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Youtube } from "lucide-react";
import { IG_CHANNELS, LOGOS, SOCIAL, YT_CHANNELS } from "@/data/content";
import { Kicker, Reveal } from "./Reveal";

const TONE = {
  green: { bg: "bg-[var(--green)]", shadow: "var(--green-deep)" },
  blue: { bg: "bg-[var(--blue)]", shadow: "var(--blue-deep)" },
};

const YtCard = ({ c, delay }) => (
  <Reveal delay={delay} className="min-w-0 lg:col-span-6">
    <motion.a
      href={c.href}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ y: -8, rotate: -0.6 }}
      transition={{ type: "spring", stiffness: 260, damping: 18 }}
      data-testid={`channel-card-${c.key}`}
      className={`felt stitch group flex h-full flex-col overflow-hidden rounded-[2.2rem] p-6 text-white sm:p-8 ${TONE[c.color].bg}`}
      style={{ boxShadow: `0 10px 0 ${TONE[c.color].shadow}` }}
    >
      <div className="flex items-start justify-between gap-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[var(--red)]">
          <Youtube className="h-4 w-4" /> YouTube
        </span>
        <span className="grid h-12 w-12 place-items-center rounded-full bg-white/20 transition-transform duration-500 group-hover:rotate-45 group-hover:bg-white group-hover:text-[var(--ink)]">
          <ArrowUpRight className="h-6 w-6" />
        </span>
      </div>
      <div className="my-6 grid place-items-center">
        <img src={c.logo} alt={c.title} className="h-44 w-auto -rotate-2 transition-transform duration-700 group-hover:rotate-2 group-hover:scale-105 sm:h-56" />
      </div>
      <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-white/85">{c.tag}</p>
      <h3 className="font-display mt-1 break-all text-2xl font-extrabold leading-tight sm:text-3xl">{c.handle}</h3>
      <p className="mt-2 text-white/90">{c.text}</p>
      <dl className="mt-6 grid grid-cols-3 gap-2 border-t-2 border-dashed border-white/40 pt-5">
        {c.stats.map((s) => (
          <div key={s.l}>
            <dt className="font-display text-2xl font-extrabold leading-none sm:text-3xl">{s.v}</dt>
            <dd className="mt-1 text-xs text-white/85">{s.l}</dd>
          </div>
        ))}
      </dl>
    </motion.a>
  </Reveal>
);

const IgCard = ({ c, delay }) => (
  <Reveal delay={delay} className="min-w-0 lg:col-span-4">
    <motion.a
      href={c.href}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ y: -6 }}
      data-testid={`social-link-instagram-${c.key}`}
      className="sticker group flex h-full items-center gap-4 rounded-[2rem] bg-white p-5"
    >
      <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white transition-transform duration-500 group-hover:rotate-12">
        <Instagram className="h-8 w-8" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-mono-label block text-[11px] uppercase tracking-[0.18em] text-[var(--ink)]/65">Instagram · {c.tag}</span>
        <span className="font-display block break-all text-lg font-extrabold leading-tight text-[var(--ink)] sm:text-xl">{c.handle}</span>
      </span>
      <ArrowUpRight className="h-6 w-6 shrink-0 text-[var(--ink)] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
    </motion.a>
  </Reveal>
);

const GamesCard = () => (
  <Reveal delay={0.2} className="lg:col-span-4">
    <motion.a
      href={SOCIAL.ytGames}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ y: -6 }}
      data-testid="channel-card-pedroclubegames"
      className="felt group flex h-full items-center gap-4 rounded-[2rem] bg-[var(--ink)] p-5 text-white"
    >
      <img src={LOGOS.games} alt="Pedro Clube Games" className="h-20 w-auto shrink-0 -rotate-3 drop-shadow-[0_8px_12px_rgba(0,0,0,0.4)] transition-transform duration-500 group-hover:rotate-3 group-hover:scale-105" />
      <span className="min-w-0 flex-1">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[var(--yellow)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[var(--ink)]">
          <Youtube className="h-3.5 w-3.5" /> YouTube · Games
        </span>
        <span className="font-display mt-1 block break-all text-xl font-extrabold">@pedroclubegames</span>
      </span>
      <ArrowUpRight className="h-6 w-6 shrink-0 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
    </motion.a>
  </Reveal>
);

export const Channels = () => (
  <section id="canais" className="relative px-5 pb-24 pt-32 sm:px-8" data-testid="channels-section">
    <div className="mx-auto max-w-6xl">
      <Reveal className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Kicker className="text-[var(--ink)]/70">Nossos canais & redes</Kicker>
          <h2 className="font-display mt-3 text-5xl font-extrabold leading-[0.95] text-[var(--ink)] sm:text-6xl">
            Aperte o play <br className="hidden sm:block" />e venha com a gente.
          </h2>
        </div>
        <p className="max-w-sm text-base text-[var(--ink)]/75 md:text-lg">Três canais no YouTube, dois perfis no Instagram — e o novo canal de games já no ar.</p>
      </Reveal>
      <div className="grid gap-6 lg:grid-cols-12">
        {YT_CHANNELS.map((c, i) => (
          <YtCard key={c.key} c={c} delay={i * 0.12} />
        ))}
        {IG_CHANNELS.map((c, i) => (
          <IgCard key={c.key} c={c} delay={0.1 + i * 0.08} />
        ))}
        <GamesCard />
      </div>
    </div>
  </section>
);
